import { useCallback, useEffect, useState } from "react";
import { ArrowLeft, Copy, Loader2, Pencil } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { request } from "../lib/api";
import type { CreatedResponse, FormField, FormFieldType, Notice } from "../types";
import { FORM_FIELD_TYPES, FORM_FIELD_TYPE_LABELS } from "../types";
import { AttributionCell } from "../components/Attribution";
import { DragList } from "../components/DragList";
import { useAttribution } from "../lib/useAttribution";
import {
  Card,
  ConfirmModal,
  InlineNotice,
  errorMessage,
  inputClass,
  labelClass,
  primaryButtonClass,
  secondaryButtonClass,
} from "../components/ui";

interface FormResponse {
  content: { fields: FormField[] };
}

const fieldHasOptions = (type: FormFieldType) => type === "single_choice" || type === "checklist" || type === "dropdown";

function emptyField(position: number): FormField {
  return { type: "short_text", label: "", position, required: false, validation: {}, options: [] };
}

export function FormBuilder() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [fields, setFields] = useState<FormField[]>([]);
  const attribution = useAttribution(
    "form-fields",
    fields.flatMap((field) => (field.id ? [field.id] : []))
  );
  const [editing, setEditing] = useState<FormField | null>(null);
  const [message, setMessage] = useState<Notice | null>(null);
  const [confirmDeleteID, setConfirmDeleteID] = useState<string | null>(null);

  const load = useCallback(() => {
    if (!id) return;
    request<FormResponse>(`/api/admin/content-items/${id}`)
      .then((data) => setFields((data.content.fields || []).sort((a, b) => a.position - b.position)))
      .catch((cause) => setMessage({ tone: "error", text: errorMessage(cause) }));
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  async function reorder(next: FormField[]) {
    setFields(next);
    try {
      await request("/api/admin/form-fields/reorder", {
        method: "POST",
        body: JSON.stringify({ form_id: id, field_ids: next.map((f) => f.id) }),
      });
    } catch (cause) {
      setMessage({ tone: "error", text: errorMessage(cause) });
      load();
    }
  }

  async function duplicate(fieldID?: string) {
    if (!fieldID) return;
    try {
      await request(`/api/admin/form-fields/${fieldID}/duplicate`, { method: "POST" });
      load();
    } catch (cause) {
      setMessage({ tone: "error", text: errorMessage(cause) });
    }
  }

  async function remove(fieldID: string) {
    try {
      await request(`/api/admin/form-fields/${fieldID}`, { method: "DELETE" });
      setConfirmDeleteID(null);
      load();
    } catch (cause) {
      setMessage({ tone: "error", text: errorMessage(cause) });
    }
  }

  return (
    <div className="grid gap-6 xl:grid-cols-[420px_minmax(0,1fr)]">
      {/* Back to wherever this was opened from: the PD day builder or the content library. */}
      <button
        type="button"
        onClick={() => navigate(-1)}
        className="inline-flex w-fit items-center gap-1 text-sm font-semibold text-interactive hover:underline xl:col-span-2"
      >
        <ArrowLeft size={16} /> Back
      </button>
      <Card title="Feedback form fields" text="Add, edit, duplicate, and reorder fields.">
        {message && <InlineNotice tone={message.tone} text={message.text} />}
        {fields.length === 0 ? (
          <button type="button" className={secondaryButtonClass} onClick={() => setEditing(emptyField(1))}>
            + Add field
          </button>
        ) : (
          <DragList
            items={fields}
            onChange={reorder}
            onRemove={(i) => setConfirmDeleteID(fields[i].id || null)}
            addLabel="Add field"
            onAdd={() => setEditing(emptyField(fields.length + 1))}
            renderItem={(f) => (
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold text-content-primary">{f.label || "(untitled field)"}</p>
                  <p className="text-xs text-content-secondary">
                    {FORM_FIELD_TYPE_LABELS[f.type]} {f.required ? "· required" : ""}
                  </p>
                  {f.id && (
                    <div className="mt-1">
                      <AttributionCell info={attribution[f.id]} />
                    </div>
                  )}
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  <button type="button" className="text-content-secondary hover:text-interactive" onClick={() => setEditing(f)} aria-label="Edit">
                    <Pencil size={15} />
                  </button>
                  <button type="button" className="text-content-secondary hover:text-interactive" onClick={() => duplicate(f.id)} aria-label="Duplicate">
                    <Copy size={15} />
                  </button>
                </div>
              </div>
            )}
          />
        )}
      </Card>
      {editing && id && (
        <FieldEditor
          formID={id}
          field={editing}
          onSaved={() => {
            setEditing(null);
            load();
          }}
          onCancel={() => setEditing(null)}
        />
      )}
      <ConfirmModal
        open={Boolean(confirmDeleteID)}
        title="Delete field?"
        message="This removes the field and any submitted responses to it."
        onConfirm={() => confirmDeleteID && remove(confirmDeleteID)}
        onCancel={() => setConfirmDeleteID(null)}
      />
    </div>
  );
}

function FieldEditor({ formID, field, onSaved, onCancel }: { formID: string; field: FormField; onSaved: () => void; onCancel: () => void }) {
  const [f, setF] = useState<FormField>(field);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<Notice | null>(null);

  function patch(next: Partial<FormField>) {
    setF((prev) => ({ ...prev, ...next }));
  }

  async function submit() {
    if (!f.label.trim()) {
      setMessage({ tone: "error", text: "Label is required." });
      return;
    }
    setBusy(true);
    setMessage(null);
    const payload = { ...f, form_id: formID };
    try {
      if (f.id) {
        await request(`/api/admin/form-fields/${f.id}`, { method: "PUT", body: JSON.stringify(payload) });
      } else {
        await request<CreatedResponse>("/api/admin/form-fields", { method: "POST", body: JSON.stringify(payload) });
      }
      onSaved();
    } catch (cause) {
      setMessage({ tone: "error", text: errorMessage(cause) });
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card title={f.id ? "Edit field" : "New field"} text="Visual builder — no JSON required.">
      <div className="grid gap-5">
        <div className="grid gap-4 sm:grid-cols-2">
          <label className={labelClass}>
            Field type
            <select
              className={inputClass}
              value={f.type}
              onChange={(e) => patch({ type: e.target.value as FormFieldType, options: fieldHasOptions(e.target.value as FormFieldType) ? [{ text: "", position: 1 }] : [] })}
            >
              {FORM_FIELD_TYPES.map((t) => (
                <option key={t} value={t}>
                  {FORM_FIELD_TYPE_LABELS[t]}
                </option>
              ))}
            </select>
          </label>
          <label className="flex items-center gap-2 pt-6 text-sm font-medium text-content-primary">
            <input type="checkbox" className="h-4 w-4 accent-interactive" checked={f.required} onChange={(e) => patch({ required: e.target.checked })} />
            Required
          </label>
        </div>
        <label className={labelClass}>
          {f.type === "informational" ? "Text to display" : "Label / question"}
          <input className={inputClass} value={f.label} onChange={(e) => patch({ label: e.target.value })} />
        </label>
        <label className={labelClass}>
          Help text (optional)
          <input className={inputClass} value={f.help_text || ""} onChange={(e) => patch({ help_text: e.target.value })} />
        </label>

        {fieldHasOptions(f.type) && (
          <div>
            <p className="mb-2 text-sm font-medium text-content-primary">Options</p>
            <DragList
              items={f.options}
              onChange={(next) => patch({ options: next.map((o, i) => ({ ...o, position: i + 1 })) })}
              onRemove={(i) => patch({ options: f.options.filter((_, idx) => idx !== i) })}
              addLabel="Add option"
              onAdd={() => patch({ options: [...f.options, { text: "", position: f.options.length + 1 }] })}
              renderItem={(opt, i) => (
                <input
                  className={inputClass}
                  value={opt.text}
                  onChange={(e) => patch({ options: f.options.map((o, idx) => (idx === i ? { ...o, text: e.target.value } : o)) })}
                />
              )}
            />
          </div>
        )}

        {f.type === "number" && (
          <div className="grid gap-4 sm:grid-cols-2">
            <label className={labelClass}>
              Minimum
              <input type="number" className={inputClass} value={(f.validation.min as number) ?? ""} onChange={(e) => patch({ validation: { ...f.validation, min: e.target.value ? Number(e.target.value) : undefined } })} />
            </label>
            <label className={labelClass}>
              Maximum
              <input type="number" className={inputClass} value={(f.validation.max as number) ?? ""} onChange={(e) => patch({ validation: { ...f.validation, max: e.target.value ? Number(e.target.value) : undefined } })} />
            </label>
          </div>
        )}
        {f.type === "rating" && (
          <label className={labelClass}>
            Scale (max value)
            <input type="number" min={2} className={inputClass} value={(f.validation.max as number) ?? 5} onChange={(e) => patch({ validation: { ...f.validation, max: Number(e.target.value) } })} />
          </label>
        )}
        {(f.type === "short_text" || f.type === "long_text") && (
          <div className="grid gap-4 sm:grid-cols-2">
            <label className={labelClass}>
              Min length
              <input type="number" min={0} className={inputClass} value={(f.validation.min_length as number) ?? ""} onChange={(e) => patch({ validation: { ...f.validation, min_length: e.target.value ? Number(e.target.value) : undefined } })} />
            </label>
            <label className={labelClass}>
              Max length
              <input type="number" min={0} className={inputClass} value={(f.validation.max_length as number) ?? ""} onChange={(e) => patch({ validation: { ...f.validation, max_length: e.target.value ? Number(e.target.value) : undefined } })} />
            </label>
          </div>
        )}

        {message && <InlineNotice tone={message.tone} text={message.text} />}
        <div className="flex items-center gap-3">
          <button type="button" className={primaryButtonClass} disabled={busy} onClick={submit}>
            {busy && <Loader2 size={16} className="animate-spin" />}Save field
          </button>
          <button type="button" className={secondaryButtonClass} onClick={onCancel}>
            Cancel
          </button>
        </div>
      </div>
    </Card>
  );
}
